import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ChevronDownStroke from '@/assets/icons/chevron_down_stroke.svg?component';
import { Badge } from '@/components/atoms/Badge';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useLayoutState } from '@/contexts/layout';

import styles from './SidebarNavItem.module.scss';

export interface SidebarNavItemProps {
    icon?: ReactNode;
    label: string;
    href?: string;
    onClick?: () => void;
    isActive?: boolean;
    isExpanded?: boolean;
    subItems?: SidebarNavItemProps[];
    highlightLabel?: string;
    className?: string;
    showChevron?: boolean;
    chevronDirection?: 'down' | 'right';
    depth?: number;
    onToggle?: () => void;
}

export default function SidebarNavItem(props: SidebarNavItemProps) {
    const {
        icon,
        label,
        href,
        onClick,
        isActive = false,
        isExpanded: controlledExpanded,
        subItems = [],
        highlightLabel,
        className,
        showChevron = false,
        chevronDirection = 'down',
        depth = 0,
        onToggle,
    } = props;

    const { sidebarCollapsed, isMobile } = useLayoutState();
    const hasSubItems = subItems.length > 0;

    // Internal expanded state (used when not controlled)
    const [internalExpanded, setInternalExpanded] = useState(false);
    const isExpanded = controlledExpanded ?? internalExpanded;

    // Show tooltip when collapsed (desktop only)
    const showTooltip = sidebarCollapsed && !isMobile && depth === 0;

    // Auto-expand if any sub-item is active
    useEffect(() => {
        if (hasSubItems && subItems.some((item) => item.isActive)) {
            setInternalExpanded(true);
        }
    }, [hasSubItems, subItems]);

    const toggleExpanded = useCallback(() => {
        if (!hasSubItems) return;
        if (onToggle) {
            onToggle();
        } else {
            setInternalExpanded((prev) => !prev);
        }
    }, [hasSubItems, onToggle]);

    // Expand parents on navigate; Link handles routing so new-tab/middle-click work.
    const handleLinkClick = useCallback(() => {
        toggleExpanded();
    }, [toggleExpanded]);

    const handleButtonClick = useCallback(() => {
        if (hasSubItems) {
            toggleExpanded();
            return;
        }
        onClick?.();
    }, [hasSubItems, onClick, toggleExpanded]);

    const isTopLevel = depth === 0;
    const shouldShowIcon = icon && isTopLevel;
    const shouldShowLabel = !sidebarCollapsed || isMobile || !isTopLevel;

    const content = (
        <>
            {isActive && isTopLevel && <div className={styles.activeIndicator} />}

            {shouldShowIcon && <div className={styles.iconWrapper}>{icon}</div>}

            {shouldShowLabel && (
                <span className={styles.labelGroup}>
                    <span className={styles.label}>{label}</span>
                    {highlightLabel != null && highlightLabel !== '' && (
                        <Badge tone="success" variant="solid" size="xs" className={styles.navBadge}>
                            {highlightLabel}
                        </Badge>
                    )}
                </span>
            )}

            {showChevron && shouldShowLabel && (
                <ChevronIcon
                    direction={hasSubItems ? 'down' : chevronDirection}
                    rotated={hasSubItems && isExpanded}
                />
            )}

            {hasSubItems && !showChevron && shouldShowLabel && (
                <ChevronIcon direction="down" rotated={isExpanded} />
            )}
        </>
    );

    const contentClassName = styles.itemContent;
    const contentTitle = showTooltip ? label : undefined;
    const contentAriaExpanded = hasSubItems ? isExpanded : undefined;
    const contentAriaCurrent = isActive ? ('page' as const) : undefined;

    return (
        <div
            className={cn(
                styles.navItem,
                isActive && styles.active,
                isTopLevel && styles.topLevel,
                isExpanded && styles.expanded,
                sidebarCollapsed && !isMobile && styles.collapsed,
                className
            )}
            data-depth={depth}>
            {href != null && href !== '' ? (
                <Link
                    to={href}
                    className={contentClassName}
                    onClick={handleLinkClick}
                    aria-expanded={contentAriaExpanded}
                    aria-current={contentAriaCurrent}
                    title={contentTitle}>
                    {content}
                </Link>
            ) : (
                <button
                    type="button"
                    className={contentClassName}
                    onClick={handleButtonClick}
                    aria-expanded={contentAriaExpanded}
                    aria-current={contentAriaCurrent}
                    title={contentTitle}>
                    {content}
                </button>
            )}

            {hasSubItems && (
                <div className={styles.subItems}>
                    <div className={styles.subItemsInner}>
                        <div className={styles.subItemsLine} />
                        {subItems.map((subItem, index) => (
                            <SidebarNavItem
                                key={subItem.href ?? `sub-${index}`}
                                {...subItem}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ChevronIcon({ direction, rotated }: { direction: 'down' | 'right'; rotated?: boolean }) {
    return (
        <SvgIcon
            icon={ChevronDownStroke}
            size="small"
            className={cn(
                styles.chevron,
                direction === 'right' && styles.chevronRight,
                rotated && styles.chevronRotated
            )}
            svgProps={{ 'aria-hidden': true }}
        />
    );
}
